import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare, Sparkles } from 'lucide-react';
import { LandingHeroPanel } from './LandingHeroPanel';

interface LandingHeroProps {
  onSignIn: () => void;
  onPrimaryAction: () => void;
}

export const LandingHero: React.FC<LandingHeroProps> = ({ onSignIn, onPrimaryAction }) => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[94vh] overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[#07111f] [background-image:linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.045)_1px,transparent_1px)] [background-size:44px_44px]"
      />

      <header className="relative z-20 flex items-center justify-between px-5 py-4 md:px-8">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
          <span className="text-base font-semibold">AppAI</span>
        </Link>

        <div className="flex items-center gap-2">
          <button
            onClick={onSignIn}
            className="rounded-lg border border-white/20 px-4 py-2 text-sm font-medium text-slate-100 transition-colors hover:border-white/40 hover:bg-white/10"
          >
            {t('common.signIn')}
          </button>
          <Link
            to="/"
            className="rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white"
          >
            {t('landing.nav.home')}
          </Link>
        </div>
      </header>

      <main className="relative z-20 px-5 pb-20 pt-14 md:px-8">
        <div className="mx-auto grid min-h-[calc(94vh-88px)] w-full max-w-7xl items-center gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(390px,1.1fr)]">
          <div className="max-w-2xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-lg border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm font-medium text-teal-100">
              <Sparkles size={16} className="flex-shrink-0" />
              {t('landing.hero.badge')}
            </div>
            <h1 className="text-5xl font-bold leading-tight md:text-7xl">AppAI</h1>
            <p className="mt-6 text-lg leading-8 text-slate-200 md:text-xl">
              {t('landing.hero.description')}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={onPrimaryAction}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-300 px-5 py-3 text-sm font-semibold text-slate-950 transition-colors hover:bg-teal-200"
              >
                {t('landing.hero.primaryCta')}
                <ArrowRight size={18} />
              </button>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-white/40 hover:bg-white/10"
              >
                {t('landing.hero.secondaryCta')}
                <MessageSquare size={18} />
              </Link>
            </div>
          </div>

          <LandingHeroPanel />
        </div>
      </main>
    </section>
  );
};
